/* eslint-disable max-lines */
// Temporary, as a bunch of flagr flags are being cleaned up in a followup PR
import { ToasterContainer } from 'baseui/toast';
import { unsafeCoerce } from 'fp-ts/lib/function';
import Head from 'next/head';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { usePreloadSlot } from '../../UnicronApp/preloader/PreloadSlot';
import { TrackingEvent } from '../../common/AnalyticsContext/TrackingEvent';
import { useDesktopTopNav } from '../../common/Experiments/DesktopTopNavExperiment';
import { logErrorToSentry } from '../../common/Sentry';
import { useUserData } from '../../common/UserProvider/UserProvider';
import { BottomNavigationBar } from '../../common/components/BottomNavigationBar/BottomNavigationBar';
import { Footer } from '../../common/components/Footer/Footer/Footer';
import { GlobalSeo } from '../../common/components/GlobalSeo';
import { HeaderLayout } from '../../common/components/SearchHeader/HeaderLayout/HeaderLayout';
import { useSearchFilters } from '../../common/components/SearchHeader/filters/useSearchFilters';
import { SPECIALTY_DENTIST } from '../../common/components/SearchHeader/types/FilterChoice';
import { PageContentContainer } from '../../common/components/layout/PageContainer';
import { duplicateSearchResults } from '../../common/duplicateSearchResults';
import { FlagrEvaluationSlot } from '../../common/flagr/preload/FlagrEvaluationSlot';
import { useEffectOnceOnCondition } from '../../common/hooks/useEffectOnceOnCondition';
import { useScrollIntoView } from '../../common/hooks/useScrollIntoView';
import { useWhiteLabelCustomisations } from '../../common/hooks/useWhiteLabelCustomisations';
import { useFlags } from '../../common/services/flags';
import { AlertBar } from '../AlertBar/AlertBar';
import { Error as ErrorComponent } from '../Error/Error';
import { FilterContainer } from '../FilterContainer/FilterContainer';
import { OverlayProvider } from '../FilterContainer/overlay/OverlayContext';
import { SearchMap } from '../MapSearch/SearchMap';
import { PreferredProviderSection } from '../PreferredProviderSection/PreferredProviderSection';
import {
  FLAGR_TELEHEALTH_GP_SEARCH_CARDS,
  TelehealthActionsVariations,
} from '../ResultView/ResultList/TelehealthActions/TelehealthActions';
import { ResultView } from '../ResultView/ResultView';
import { SearchFooter } from '../SearchFooter/SearchFooter';
import { FLAGR_MAP_SEARCH } from '../constants';
import { useSeoContent } from '../page-seo/SeoContentProvider';
import { parseProfile } from '../parseProfile';
import { useTrackMediaAds } from '../search-ad/useTrackMediaAds';
import { useCanonicalNextAvailablePath } from '../useCanonicalPath';
import { useSearchQuery } from '../useSearchQuery/useSearchQuery';
import { CovidRedirectToast } from './CovidRedirectToast/CovidRedirectToast';
import { DEFAULT_SEARCH_RADIUS } from './DEFAULT_SEARCH_RADIUS';
import { SearchPageContainer } from './SearchPageStyles';
import { extendSearch } from './extendSearch';
import { isExtendedSearchPossible } from './isExtendedSearchPossible';
import { useDfpTargeting } from './useDfpTargeting';
import { useGamAds } from './useGamAds';
import { useSearchPageAnalytics } from './useSearchPageAnalytics/useSearchPageAnalytics';
import { addSuggestion, useSuggestionCards } from './useSuggestionCards';

export const SearchPage = () => {
  useTrackMediaAds();
  const [filters] = useSearchFilters();
  const { showFooter } = useWhiteLabelCustomisations();
  const userData = useUserData();
  const flags = useFlags();
  const originalCovidVaccinePractice = filters.extraQuery?.originalCovidVaccinePractice;

  // eslint-disable-next-line no-console
  if (flags.logFilters) console.log(filters);

  const canonicalPath = useCanonicalNextAvailablePath(filters);

  const { loading, error, data, fetchMore, fetchingMore } = useSearchQuery(
    filters,
    undefined,
    undefined,
    DEFAULT_SEARCH_RADIUS,
  );

  const { results } = data?.searchProfiles ?? {};

  const searchResults = useMemo(() => (results ?? []).flatMap(it => parseProfile(it)), [results]);

  const searchSpecialty = data?.searchSpecialty?.name;
  const totalCount = data?.searchProfiles.totalCount;

  const extendedSearchPossible = isExtendedSearchPossible(totalCount, filters);

  const {
    loading: extendedLoading,
    data: extendedData,
    fetchMore: extendedFetchMore,
    fetchingMore: extendedFetchingMore,
  } = useSearchQuery(extendSearch(filters), undefined, !extendedSearchPossible);

  const { trackEvent } = useSearchPageAnalytics({
    filters,
    typesFacet: data?.searchProfiles.facetsWithCounts.types.values,
    profiles: searchResults,
    total: data?.searchProfiles.totalCount ?? 0,
    searchSpecialty,
    extendedResults: extendedData,
  });

  useEffectOnceOnCondition(Boolean(error), () => {
    logErrorToSentry(new Error(`Search GraphQL failure: ${error?.name} ${error?.message}`), {
      cause: error,
    });
  });

  useDfpTargeting(filters);

  /** A stable string key which only changes when the list of displayed results changes. */
  const searchResultKeys = useMemo(() => {
    void null;
    return (results ?? [])
      .concat(extendedData?.searchProfiles.results ?? [])
      .map(it => it.appointmentSummary.id)
      .join('/');
  }, [extendedData?.searchProfiles.results, results]);

  useEffect(() => {
    /** We delay firing this event while any relevant data is still loading */
    if (loading || userData.loading) return;

    /** We delay the event if the current search requires extended-search data, but it's not
     * loaded in yet
     */
    if (extendedSearchPossible && extendedLoading) return;

    /** We refer to searchResultKeys here as a no-op so this effect re-runs whenever it changes */
    void searchResultKeys;

    /** This event should fire exactly once every time the displayed results change, and only after
     * they are fully loaded. */
    trackEvent(TrackingEvent.SEARCH_PERFORMED);
  }, [
    extendedLoading,
    loading,
    searchResultKeys,
    extendedSearchPossible,
    trackEvent,
    userData.loading,
  ]);

  const displayError = error ?? (flags.forceError && 'Forced testing error');

  const seoContent = useSeoContent();

  const profilesSuggestions = useSuggestionCards(searchResults, filters, searchSpecialty);

  useEffect(() => {
    if (duplicateSearchResults(profilesSuggestions)) {
      logErrorToSentry(new Error('duplicate profile suggestions returned'), {
        suggestions: profilesSuggestions,
      });
    }
  }, [profilesSuggestions]);

  const profileGamAds = useGamAds(profilesSuggestions);

  // PhiSlide added after the second card
  const profilePhiSlide = addSuggestion({ type: 'phiSlide' }, profileGamAds, 2);

  const flagrEvaluationMap = usePreloadSlot(FlagrEvaluationSlot);

  const mapSearchEnabled =
    flags.enableMapSearch || Boolean(flagrEvaluationMap[FLAGR_MAP_SEARCH]?.variantKey);

  const telehealthSearchCardsVariant = unsafeCoerce<
    string | undefined,
    TelehealthActionsVariations | undefined
  >(flagrEvaluationMap[FLAGR_TELEHEALTH_GP_SEARCH_CARDS]?.variantKey);

  const [mapSearchExpanded, setMapSearchExpanded] = useState(false);
  const [preferredProvidersHidden, setPreferredProvidersHidden] = useState(false);

  const filterOffset =
    filters.topic?.specialist !== SPECIALTY_DENTIST ||
    preferredProvidersHidden ||
    filters.insurance !== undefined;

  const toggleMapSearch = () => {
    setMapSearchExpanded(!mapSearchExpanded);
  };

  const [listTopRef, scrollToTop] = useScrollIntoView<HTMLDivElement>('end');

  const firstProfileId = searchResults[0]?.id;

  const isFirstRender = useRef(true);

  // We take the change of the top result as evidence that the search has changed.
  // Loading more results will not trigger a scroll to top.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      scrollToTop();
    }
  }, [firstProfileId, scrollToTop]);

  const topNavExperiment = useDesktopTopNav();

  return (
    <>
      {originalCovidVaccinePractice && (
        <ToasterContainer>
          <CovidRedirectToast practiceId={String(originalCovidVaccinePractice)} />
        </ToasterContainer>
      )}

      <AlertBar />
      <OverlayProvider>
        <SearchPageContainer>
          <div ref={listTopRef} />
          <HeaderLayout topNavExperiment={topNavExperiment} showSearchExperiment={true}>
            <Head>
              <title>{seoContent?.pageTitle}</title>
              <meta name="description" content={seoContent?.metaDescription} />
              <meta name="viewport" content="initial-scale=1.0,user-scalable=no" />
              <link
                href="https://api.mapbox.com/mapbox-gl-js/v2.5.0/mapbox-gl.css"
                rel="stylesheet"
              />
            </Head>
            <GlobalSeo absoluteUrl={canonicalPath} />
            <FilterContainer
              searchFacets={data?.searchProfiles?.facetsWithCounts}
              loading={loading}
              toggleMapSearch={toggleMapSearch}
              mapSearchEnabled={mapSearchEnabled}
            />
          </HeaderLayout>
          <PageContentContainer topNavExperiment={topNavExperiment}>
            {displayError && <ErrorComponent error={displayError} />}

            {!displayError && (
              <>
                {filters.topic?.specialist === SPECIALTY_DENTIST && (
                  <PreferredProviderSection
                    searchFacets={data?.searchProfiles?.facetsWithCounts}
                    setPreferredProvidersHidden={setPreferredProvidersHidden}
                    preferredProvidersHidden={preferredProvidersHidden}
                  />
                )}
                {mapSearchEnabled && (
                  <SearchMap searchResults={profilePhiSlide} expanded={mapSearchExpanded} />
                )}
                <ResultView
                  totalCount={totalCount}
                  searchResults={profilePhiSlide}
                  onClickMore={fetchMore}
                  fetchingMore={fetchingMore}
                  loading={loading}
                  filters={filters}
                  searchSpecialty={searchSpecialty}
                  showingExtended={extendedSearchPossible}
                  extendedResults={extendedData}
                  extendedFetchMore={extendedFetchMore}
                  extendedFetchingMore={extendedFetchingMore}
                  useFilterOffset={filterOffset}
                  telehealthSearchCardsVariant={telehealthSearchCardsVariant}
                />
              </>
            )}
          </PageContentContainer>
          <SearchFooter filters={filters} />
          {showFooter && <Footer />}
          <BottomNavigationBar />
        </SearchPageContainer>
      </OverlayProvider>
    </>
  );
};
